import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Ban, KeyRound, LogOut, Plus, Search, Shield, Trash2, User } from "lucide-react";
import { api, apiError, formatDate } from "@/lib/api";
import { useConfirm } from "@/components/Confirm";
import { useAuth } from "@/context/AuthContext";
import { useTenant } from "@/context/TenantContext";
import { cacheGet, cacheKey, cacheSet } from "@/lib/cache";
import { useDelayedLoading } from "@/lib/useDelayedLoading";
import Layout from "@/components/Layout";
import { LoadingPanel } from "@/components/Bits";
import { BulkActionBar, SelectBox, runBulk, useSelection } from "@/components/BulkSelect";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";

export default function Team() {
  const confirm = useConfirm();
  const { user } = useAuth();
  const { tenantId } = useTenant();
  const CK = cacheKey("team", tenantId);
  const cached = cacheGet(CK);
  // Seeded from cache so revisiting (even after a reload) shows the list
  // instantly instead of flashing a loading skeleton — see lib/cache.js.
  const [rows, setRows] = useState(() => cached || []);
  const [loading, setLoading] = useState(() => !cached);
  // Only show a loading skeleton once a fetch has actually taken a moment —
  // a cached/fast load resolves before the delay and never flashes one.
  const showLoading = useDelayedLoading(loading);
  const [adding, setAdding] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "agent" });
  const [q, setQ] = useState("");

  const load = useCallback(async () => {
    if (!cacheGet(CK)) setLoading(true);
    try {
      const { data } = await api.get("/admin/users");
      setRows(data);
      cacheSet(CK, data);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [CK]);

  useEffect(() => {
    load();
  }, [load]);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return rows;
    return rows.filter(
      (r) => (r.name || "").toLowerCase().includes(term) || (r.email || "").toLowerCase().includes(term)
    );
  }, [rows, q]);

  // Bulk select — you can't select yourself, same as the per-row Delete
  // button being hidden for your own row: removing your own account from
  // here would lock you out.
  const selection = useSelection();
  const eligibleRows = useMemo(() => filtered.filter((r) => r.user_id !== user?.user_id), [filtered, user]);
  const selectedRows = useMemo(
    () => eligibleRows.filter((r) => selection.isSelected(r.user_id)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [eligibleRows, selection.selected]
  );
  const allVisibleSelected = eligibleRows.length > 0 && eligibleRows.every((r) => selection.isSelected(r.user_id));
  const toggleSelectAll = () => selection.setMany(eligibleRows.map((r) => r.user_id), !allVisibleSelected);

  const create = async () => {
    if (!form.name || !form.email || form.password.length < 8) {
      toast.error("Fill in every field, with at least 8 characters for the password");
      return;
    }
    setBusy(true);
    try {
      await api.post("/admin/users", form);
      toast.success(`${form.name} can now sign in`);
      setAdding(false);
      setForm({ name: "", email: "", password: "", role: "agent" });
      load();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  const changeRole = async (row, role) => {
    try {
      await api.patch(`/admin/users/${row.user_id}/role`, { role });
      toast.success(`${row.name} is now ${role === "admin" ? "an admin" : "an agent"}`);
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  // Until email is configured this is how someone locked out gets back in:
  // you generate the link and pass it to them.
  const makeResetLink = async (row) => {
    try {
      const { data } = await api.post(`/admin/users/${row.user_id}/reset-link`);
      try {
        await navigator.clipboard.writeText(data.link);
        toast.success(`Reset link copied — send it to ${row.name}. Valid ${data.expires_in_minutes} minutes.`);
      } catch {
        window.prompt(`Copy this link and send it to ${row.name}:`, data.link);
      }
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const togglePin = async (row, next) => {
    try {
      await api.patch(`/admin/users/${row.user_id}/pin`, { pin_enabled: next });
      toast.success(next ? `PIN sign-in allowed for ${row.name}` : `PIN sign-in turned off for ${row.name}`);
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const forceLogout = async (row) => {
    const ok = await confirm({
      title: `Log ${row.name} out?`,
      body: "Ends every active session for them and clears any devices they'd saved a PIN on — they'll need their password to sign back in.",
      confirmText: "Log out",
      destructive: true,
    });
    if (!ok) return;
    try {
      await api.post(`/admin/users/${row.user_id}/logout`);
      toast.success(`${row.name} is signed out everywhere`);
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const toggleActive = async (row) => {
    const disabling = row.is_active !== false;
    if (disabling) {
      const ok = await confirm({
        title: `Disable ${row.name}?`,
        body: "They're signed out immediately and can't sign back in — with a password or a PIN — until you turn this back on.",
        confirmText: "Disable",
        destructive: true,
      });
      if (!ok) return;
    }
    try {
      await api.post(`/admin/users/${row.user_id}/${disabling ? "disable" : "enable"}`);
      toast.success(disabling ? `${row.name} is disabled` : `${row.name} can sign in again`);
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const remove = async (row) => {
    const ok = await confirm({
      title: `Remove ${row.name}?`,
      body: "They'll be signed out immediately and won't be able to sign back in.",
      confirmText: "Remove",
      destructive: true,
    });
    if (!ok) return;
    try {
      await api.delete(`/admin/users/${row.user_id}`);
      toast.success("Account removed");
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const bulkRemove = async () => {
    const ok2 = await confirm({
      title: `Remove ${selectedRows.length} ${selectedRows.length === 1 ? "person" : "people"}?`,
      body: "This removes them from your team. They'll be signed out immediately and won't be able to sign back in.",
      confirmText: "Remove",
      destructive: true,
    });
    if (!ok2) return;
    const { ok, failed } = await runBulk(selectedRows, (r) => api.delete(`/admin/users/${r.user_id}`));
    if (ok) toast.success(`${ok} account${ok === 1 ? "" : "s"} removed`);
    if (failed) toast.error(`${failed} couldn't be removed`);
    selection.clear();
    load();
  };

  return (
    <Layout
      title="Team"
      subtitle="Logins for this client. Admins change settings, manage the team, and can see every conversation; agents see only the conversations they're part of."
      action={
        <Button onClick={() => setAdding(true)} className="bg-brand hover:bg-brand-hover">
          <Plus className="w-4 h-4 sm:mr-2" />
          <span className="hidden sm:inline">Add person</span>
        </Button>
      }
    >
      {rows.length > 0 &&
        (selectedRows.length > 0 ? (
          <BulkActionBar
            count={selectedRows.length}
            noun={selectedRows.length === 1 ? "person selected" : "people selected"}
            onClear={selection.clear}
            actions={[{ key: "delete", label: "Delete", icon: Trash2, tone: "danger", onClick: bulkRemove }]}
          />
        ) : (
          <div className="mb-4 relative max-w-sm w-full">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted2" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search team…" className="pl-9" />
          </div>
        ))}
      {loading ? (
        showLoading ? <LoadingPanel rows={4} /> : null
      ) : filtered.length === 0 ? (
        <div className="panel panel-pad text-center text-[13px] text-muted2 max-w-4xl">
          No one matches “{q}”.
        </div>
      ) : (
        <div className="panel overflow-hidden max-w-5xl">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[820px]">
              <thead>
                <tr className="border-b border-line bg-surface/60">
                  <th className="t-head px-4 py-3 text-left w-[38px]">
                    <SelectBox checked={allVisibleSelected} onClick={toggleSelectAll} />
                  </th>
                  {["Name", "Email", "Role", "PIN sign-in", "Status", "Joined", ""].map((h, i) => (
                    <th key={h + i} className="t-head px-4 py-3 text-left">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map((r) => {
                  const isSelf = r.user_id === user?.user_id;
                  return (
                    <tr
                      key={r.user_id}
                      className={cn("t-row last:border-0 group", selection.isSelected(r.user_id) && "bg-brand-light/40")}
                    >
                      <td className="t-cell">
                        {!isSelf && (
                          <SelectBox checked={selection.isSelected(r.user_id)} onClick={() => selection.toggle(r.user_id)} />
                        )}
                      </td>
                      <td className="t-cell">
                        <div className="flex items-center gap-2.5">
                          <div className="w-8 h-8 rounded-full bg-brand-light flex items-center justify-center font-display font-semibold text-brand text-[12px] shrink-0">
                            {(r.name?.[0] || "U").toUpperCase()}
                          </div>
                          <span className="font-medium">
                            {r.name}
                            {isSelf && <span className="text-muted2 font-normal"> (you)</span>}
                          </span>
                        </div>
                      </td>
                      <td className="t-cell text-muted2">{r.email}</td>
                      <td className="t-cell">
                        <Select
                          value={r.role}
                          onValueChange={(v) => changeRole(r, v)}
                          disabled={isSelf}
                        >
                          <SelectTrigger className="w-[118px] h-8 text-[13px] border-line">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="admin">
                              <span className="inline-flex items-center gap-1.5">
                                <Shield className="w-3.5 h-3.5" /> Admin
                              </span>
                            </SelectItem>
                            <SelectItem value="agent">
                              <span className="inline-flex items-center gap-1.5">
                                <User className="w-3.5 h-3.5" /> Agent
                              </span>
                            </SelectItem>
                          </SelectContent>
                        </Select>
                      </td>
                      <td className="t-cell">
                        <Switch
                          checked={!!r.pin_enabled}
                          onCheckedChange={(v) => togglePin(r, v)}
                          aria-label={`Allow PIN sign-in for ${r.name}`}
                        />
                      </td>
                      <td className="t-cell">
                        <span
                          className={cn(
                            "inline-flex items-center rounded-full px-2 py-0.5 text-[12px] font-medium",
                            r.is_active === false
                              ? "bg-stock-out/10 text-stock-out"
                              : "bg-emerald-50 text-emerald-700"
                          )}
                        >
                          {r.is_active === false ? "Disabled" : "Active"}
                        </span>
                      </td>
                      <td className="t-cell text-muted2 whitespace-nowrap">
                        {formatDate(r.created_at)}
                      </td>
                      <td className="t-cell text-right whitespace-nowrap">
                        <button
                          onClick={() => makeResetLink(r)}
                          className="text-muted2 hover:text-brand sm:opacity-40 group-hover:opacity-100 focus:opacity-100 transition-opacity p-1 mr-1"
                          title="Copy a password reset link for this person"
                        >
                          <KeyRound className="w-4 h-4" />
                        </button>
                        {!isSelf && (
                          <button
                            onClick={() => forceLogout(r)}
                            className="text-muted2 hover:text-brand sm:opacity-40 group-hover:opacity-100 focus:opacity-100 transition-opacity p-1 mr-1"
                            title="Log out everywhere"
                          >
                            <LogOut className="w-4 h-4" />
                          </button>
                        )}
                        {!isSelf && (
                          <button
                            onClick={() => toggleActive(r)}
                            className="text-muted2 hover:text-brand sm:opacity-40 group-hover:opacity-100 focus:opacity-100 transition-opacity p-1 mr-1"
                            title={r.is_active === false ? "Enable" : "Disable"}
                          >
                            <Ban className="w-4 h-4" />
                          </button>
                        )}
                        {!isSelf && (
                          <button
                            onClick={() => remove(r)}
                            className="text-muted2 hover:text-stock-out sm:opacity-40 group-hover:opacity-100 focus:opacity-100 transition-opacity p-1"
                            title="Remove"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <Dialog open={adding} onOpenChange={setAdding}>
        <DialogContent className="sm:max-w-[420px]">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">Add someone to the team</DialogTitle>
            <DialogDescription>
              They'll be asked to choose their own password the first time they sign in.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-1">
            <div className="space-y-1.5">
              <Label htmlFor="tn">Name</Label>
              <Input
                id="tn"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="te">Email</Label>
              <Input
                id="te"
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="tp">Temporary password</Label>
              <Input
                id="tp"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
              />
              <p className="text-[12px] text-muted2">At least 8 characters.</p>
            </div>
            <div className="space-y-1.5">
              <Label>Role</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="agent">Agent</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setAdding(false)}>
              Cancel
            </Button>
            <Button onClick={create} disabled={busy} className="bg-brand hover:bg-brand-hover">
              Add person
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
